import styled from "@emotion/styled";
import memoizeOne from "memoize-one";
import { useEffect, useRef } from "react";
import * as React from "react";

import { DesignToken, Tooltip } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { Body, Header, TextBody, Title } from "components/popper-container";
import { ratingToTextMap } from "constants/rating";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { timeAgo } from "helpers/date";
import { isRichTextMessageEmpty } from "helpers/text-editor";
import {
  getTicketSummary as getTicketSummaryHelper,
  parseEventMessageToHtml,
  parseRichTextFormatToHtml,
} from "helpers/tickets";
import { Author, IEvent, IEventAuthor, ITicket } from "interfaces/entities/ticket";
import { IAgent } from "store/entities/agents";
import { withVar } from "themes/helpers";

import { MergedTicketPreview } from "./MergedTicketPreview";

interface IProps {
  ticket: ITicket;
  agents: IAgent[];
  children: React.ReactNode;
}

const StyledTextBody = styled(TextBody)`
  max-height: 100px;
  overflow: hidden;

  ul {
    margin-left: 20px;
    list-style: disc;
  }

  a {
    color: ${withVar(DesignToken.ActionPrimaryDefault)};
  }

  img {
    max-width: 500px;
    height: auto;
  }
`;

function calculateTicketSummary(ticket: ITicket): { subject: string; firstMessage: IEvent; lastMessage: IEvent } {
  const ticketSummary = getTicketSummaryHelper(ticket);
  const { firstMessage } = ticketSummary;
  let { lastMessage } = ticketSummary;
  const { subject } = ticket;

  if (firstMessage === lastMessage) {
    // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'IEvent | un... Remove this comment to see the full error message
    lastMessage = null;
  }

  // @ts-expect-error TS(2322): Type 'IEvent | undefined' is not assignable to typ... Remove this comment to see the full error message
  return { subject, firstMessage, lastMessage };
}
const getTicketSummary = memoizeOne((ticket: ITicket) => calculateTicketSummary(ticket));

function getAuthorName(agents: IAgent[], author: IEventAuthor | null): string | null {
  if (!author) {
    return null;
  }

  if (author.type === Author.Customer) {
    // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    return author.name || author.email;
  }
  if (author.type === Author.Agent) {
    const agentId = author.id;
    const agentNameFallback = author.name;
    const agent = agents.find((agentItem: IAgent) => agentItem.id === agentId);
    // @ts-expect-error TS(2322)
    return agent ? agent.name : agentNameFallback;
  }

  return null;
}

const emptyMessageFallback = "This message has no content.";

function messageIsEmpty(event: IEvent): boolean {
  if (event.rating || event.ratingRequest) {
    return false;
  }

  if (event.messageText) {
    const parsedMessage = parseEventMessageToHtml(event.messageText);
    return parsedMessage.length === 0 && !event.attachments;
  }

  if (event.messageHtml) {
    const domParser = new DOMParser();
    const parsed = domParser.parseFromString(event.messageHtml, "text/html");
    return parsed.body.innerText.trim().length === 0 && !event.attachments;
  }

  if (event.messageRichText) {
    return isRichTextMessageEmpty(event.messageRichText) && !event.attachments;
  }

  return false;
}

function getMessagePreview(event: IEvent): React.ReactElement {
  if (event.ratingRequest) {
    return <p>{event.ratingRequest.text}</p>;
  }

  if (event.rating) {
    return (
      <>
        <p>
          This ticket was rated as <strong>{ratingToTextMap[event.rating.new.rate]}</strong>
        </p>
        {event.rating.new.comment && (
          <p>
            <em>{event.rating.new.comment}</em>
          </p>
        )}
      </>
    );
  }

  if (event.messageText) {
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: parseEventMessageToHtml(event.messageText),
        }}
      />
    );
  }

  if (event.messageHtml) {
    return <span dangerouslySetInnerHTML={{ __html: event.messageHtml }} />;
  }

  if (event.messageRichText) {
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: parseRichTextFormatToHtml(event.messageRichText),
        }}
      />
    );
  }

  return (
    <span>
      <em>Preview not available.</em>
    </span>
  );
}

interface IMessage {
  agents: IAgent[];
  event: IEvent;
  label: string;
  withSeparator?: boolean;
}

const Message: React.FC<IMessage> = ({ agents, event, label, withSeparator }) => {
  const author = getAuthorName(agents, event.author ?? null);
  return (
    <>
      <Body withSeparator={withSeparator}>
        <Title>
          {label} {author ? `by ${author}` : ""}
        </Title>
        <StyledTextBody>
          {messageIsEmpty(event) && <>{emptyMessageFallback}</>}
          {!messageIsEmpty(event) && getMessagePreview(event)}
          {event.attachments && (
            <ul>
              {event.attachments.map(attachment => (
                <li key={attachment.url}>{attachment.name}</li>
              ))}
            </ul>
          )}
        </StyledTextBody>
      </Body>
    </>
  );
};

const ENGAGEMENT_TRACKING_TIMEOUT = 1500;

export const TicketsListPreview: React.FC<IProps> = ({ ticket, agents, children }) => {
  const { subject, firstMessage, lastMessage } = getTicketSummary(ticket);
  const engagementTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    engagementTimeoutRef.current = setTimeout(() => {
      trackEngagement("Tickets list: Preview displayed", EventPlace.Tickets);
    }, ENGAGEMENT_TRACKING_TIMEOUT);

    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => {
      if (engagementTimeoutRef.current) {
        clearTimeout(engagementTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Tooltip placement="bottom-start" isVisible triggerRenderer={() => children} css={{ maxWidth: "430px" }}>
      <Block width="400px" minWidth="200px">
        <Header withSeparator>
          <Title empty={!subject}>{subject || <>No subject</>}</Title>
          <Title floated>{timeAgo(firstMessage.date, new Date())}</Title>
        </Header>
        {(ticket.parentTicket || ticket.childTickets.length > 0) && <MergedTicketPreview ticket={ticket} />}
        {firstMessage && <Message agents={agents} event={firstMessage} label="First message" />}
        {lastMessage && <Message agents={agents} event={lastMessage} label="Last message" withSeparator />}
      </Block>
    </Tooltip>
  );
};
